import { $, $$, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { confirmAction, toastError, toastSuccess } from '../lib/toast.js';

/**
 * The marketplace: proposing a configuration, and installing one.
 *
 * Nothing here builds the preset itself. The page sends which rules to export
 * and the server reads them from its own database — a browser that could hand
 * over a payload could hand over one that does not match what it displayed.
 */
export function initMarketplacePages(): void {
  proposal();
  installer();
  authorEdit();
  withdraw();
  likes();
  moduleSwitch();
}

/**
 * Liking, from the catalogue and from the page.
 *
 * The count comes back from the server rather than being incremented here: two
 * tabs open on the same catalogue would otherwise disagree, and the one that is
 * wrong is the one somebody is looking at.
 */
function likes(): void {
  for (const button of $$<HTMLElement>('[data-like]')) {
    button.addEventListener('click', () => {
      void (async () => {
        const slug = button.dataset.like ?? '';
        const guildId = new URLSearchParams(location.search).get('guildId') ?? '';

        try {
          const { liked } = await api.post<{ liked: boolean }>(
            `/api/marketplace/${slug}/like?guildId=${guildId}`,
          );

          const count = button.querySelector('[data-like-count]');
          // Digits only: a formatted count ("1 234") would otherwise become NaN.
          const current = Number.parseInt((count?.textContent ?? '').replace(/\D/g, ''), 10) || 0;

          if (count) count.textContent = String(Math.max(0, current + (liked ? 1 : -1)));
          button.setAttribute('aria-pressed', liked ? 'true' : 'false');
          const heart = button.firstChild;
          if (heart) heart.textContent = liked ? '♥ ' : '♡ ';
        } catch (error) {
          toastError(error);
        }
      })();
    });
  }
}

/**
 * Choosing another module shows its own list.
 *
 * Every module's items are already on the page, so this is a class rather than
 * a page load — reloading lost whatever had been typed above it, which reads as
 * the form throwing your work away.
 */
let chosenModule = '';

function moduleSwitch(): void {
  const select = $<HTMLSelectElement>('[data-module-switch]');
  if (!select) return;

  chosenModule = select.value;

  select.addEventListener('change', () => {
    chosenModule = select.value;

    for (const list of $$<HTMLElement>('[data-module-items]')) {
      const mine = list.dataset.moduleItems === chosenModule;
      list.classList.toggle('hidden', !mine);

      // Boxes ticked in a list nobody can see any more would be sent with the
      // submission, and the author would never know what they published.
      if (!mine) {
        for (const box of list.querySelectorAll<HTMLInputElement>('[data-rule-id]')) {
          box.checked = false;
        }
      }
    }

    /*
     * The purpose follows the module.
     *
     * Where a module answers it by itself, the menu goes away and the sentence
     * saying which purpose it landed on takes its place.
     */
    let settled = false;

    for (const note of $$<HTMLElement>('[data-module-category]')) {
      const mine = note.dataset.moduleCategory === chosenModule;
      note.classList.toggle('hidden', !mine);
      settled ||= mine;
    }

    $<HTMLElement>('[data-category-picker]')?.classList.toggle('hidden', settled);
  });
}

/** The submission form: pick rules, describe them. */
function proposal(): void {
  const form = $<HTMLFormElement>('[data-preset-form]');
  if (!form) return;

  const guildId = form.dataset.guildId ?? '';

  trackDirtyForm({
    form,
    successMessage: ct('presetSubmitted', 'Sent for review.'),
    onSave: async () => {
      const visible = $<HTMLElement>(`[data-module-items="${chosenModule}"]`, form);
      const ruleIds = [...(visible?.querySelectorAll<HTMLInputElement>('[data-rule-id]') ?? [])]
        .filter((box) => box.checked)
        .map((box) => box.dataset.ruleId ?? '');

      if (ruleIds.length === 0) {
        toastError(new Error(ct('presetNoRule', 'Choose at least one rule.')));
        return;
      }

      const { slug } = await api.post<{ slug: string }>(`/api/marketplace?guildId=${guildId}`, {
        guildId,
        module: chosenModule || pageData<{ module: string }>().module,
        category: select(form, 'category'),
        title: value(form, 'title'),
        summary: value(form, 'summary'),
        description: value(form, 'description'),
        ruleIds,
      });

      window.location.href = `/marketplace/${slug}?guildId=${guildId}`;
    },
  });
}

/** The install form: answer each slot, then install. */
function installer(): void {
  const form = $<HTMLFormElement>('[data-install-form]');
  if (!form) return;

  const guildId = form.dataset.guildId ?? '';
  const slug = form.dataset.slug ?? '';

  $<HTMLButtonElement>('[data-install]')?.addEventListener('click', () => {
    void (async () => {
      const answers: Record<string, string> = {};

      for (const field of $$<HTMLSelectElement>('[data-slot]', form)) {
        answers[field.dataset.slot ?? ''] = field.value;
      }

      if (Object.values(answers).some((value) => value === '')) {
        toastError(new Error(ct('presetAnswerAll', 'Answer every question first.')));
        return;
      }

      try {
        const { count } = await api.post<{ count: number }>(
          `/api/marketplace/${slug}/install?guildId=${guildId}`,
          { guildId, answers },
        );

        toastSuccess(
          ct('presetInstalled', '{count} rules added, switched off.', { count: String(count) }),
        );
        window.location.href = `/modules/automations?guildId=${guildId}`;
      } catch (error) {
        toastError(error);
      }
    })();
  });

  $<HTMLButtonElement>('[data-report]')?.addEventListener('click', () => {
    void (async () => {
      if (!(await confirmAction(ct('presetReportConfirm', 'Report this preset?')))) return;

      try {
        await api.post(`/api/marketplace/${slug}/report?guildId=${guildId}`, {
          guildId,
          reason: '',
        });
        toastSuccess(ct('presetReported', 'Reported. Somebody will read it.'));
      } catch (error) {
        toastError(error);
      }
    })();
  });
}

/** The author's own edits, while the preset waits to be read. */
function authorEdit(): void {
  const form = $<HTMLFormElement>('[data-preset-edit]');
  if (!form) return;

  const guildId = form.dataset.guildId ?? '';
  const slug = form.dataset.slug ?? '';

  trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: async () => {
      const slots = $$<HTMLInputElement>('[data-slot-label]', form).map((field) => ({
        key: field.dataset.slotLabel ?? '',
        label: field.value,
      }));

      await api.patch(`/api/marketplace/${slug}?guildId=${guildId}`, {
        guildId,
        title: value(form, 'title'),
        summary: value(form, 'summary'),
        description: value(form, 'description'),
        category: select(form, 'category'),
        changeNote: value(form, 'changeNote'),
        slots,
      });

      location.reload();
    },
  });
}

/** Withdrawing one's own proposal, published or not. */
function withdraw(): void {
  const button = $<HTMLElement>('[data-preset-delete]');
  if (!button) return;

  button.addEventListener('click', () => {
    void (async () => {
      if (!(await confirmAction(ct('presetWithdrawConfirm', 'Withdraw this preset?')))) return;

      const slug = button.dataset.presetDelete ?? '';
      const guildId = new URLSearchParams(location.search).get('guildId') ?? '';

      try {
        await api.delete(`/api/marketplace/${slug}?guildId=${guildId}`);
        toastSuccess(ct('presetWithdrawn', 'Withdrawn from the catalogue.'));
        location.href = `/marketplace?guildId=${guildId}`;
      } catch (error) {
        toastError(error);
      }
    })();
  });
}

function select(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLSelectElement ? field.value : '';
}

function value(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
    ? field.value.trim()
    : '';
}
